import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SchedulePickerDialog, { formatLisbon } from '@/components/SchedulePickerDialog';
import { Calendar, CheckCircle2, Rocket, X } from 'lucide-react';

function StatusPill({ item, publishedFlag }) {
  const isScheduled = !!item?.scheduled_at;
  const isPublished = publishedFlag(item);
  if (isScheduled) {
    return (
      <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/40 gap-1" data-testid="sched-status-scheduled">
        <Calendar className="w-3 h-3" /> Agendado · {formatLisbon(item.scheduled_at)}
      </Badge>
    );
  }
  if (isPublished) {
    return (
      <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 gap-1" data-testid="sched-status-published">
        <CheckCircle2 className="w-3 h-3" /> Publicado
      </Badge>
    );
  }
  return (
    <Badge className="bg-zinc-500/20 text-zinc-300 border border-zinc-500/40" data-testid="sched-status-draft">
      Rascunho
    </Badge>
  );
}

/**
 * Row-level scheduling control for an EXISTING content record.
 * For NEW records use <SchedulePickerDialog /> directly and pass `scheduled_at`
 * in the create payload.
 */
export default function SchedulingControls({ resource, item, publishedFlag, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isScheduled = !!item?.scheduled_at;
  const isPublished = publishedFlag(item);

  const scheduleViaApi = async (isoUtc) => {
    try {
      await api.post(`/admin/scheduling/${resource}/${item.id}/schedule`, { scheduled_at: isoUtc });
      toast.success(`Publicação agendada para ${formatLisbon(isoUtc)}`);
      setOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao agendar');
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/scheduling/${resource}/${item.id}/cancel`);
      toast.success('Agendamento cancelado');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao cancelar');
    } finally { setBusy(false); }
  };

  const publishNow = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/scheduling/${resource}/${item.id}/publish-now`);
      toast.success('Publicado');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao publicar');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`sched-controls-${item.id}`}>
      <StatusPill item={item} publishedFlag={publishedFlag} />

      {!isPublished && !isScheduled && (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={busy} data-testid={`sched-schedule-btn-${item.id}`}>
          <Calendar className="w-3.5 h-3.5 mr-1" /> Agendar
        </Button>
      )}
      {isScheduled && (
        <>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={busy} data-testid={`sched-edit-btn-${item.id}`}>
            <Calendar className="w-3.5 h-3.5 mr-1" /> Editar
          </Button>
          <Button size="sm" variant="ghost" onClick={cancel} disabled={busy} data-testid={`sched-cancel-btn-${item.id}`}>
            <X className="w-3.5 h-3.5 mr-1" /> Cancelar
          </Button>
          <Button size="sm" className="bg-ruby hover:bg-ruby-light text-white" onClick={publishNow} disabled={busy} data-testid={`sched-publish-btn-${item.id}`}>
            <Rocket className="w-3.5 h-3.5 mr-1" /> Publicar já
          </Button>
        </>
      )}

      <SchedulePickerDialog
        open={open}
        onOpenChange={setOpen}
        description={item?.title}
        initialIso={item?.scheduled_at}
        onConfirm={scheduleViaApi}
      />
    </div>
  );
}
